/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { getRefreshToken } from '../../helpers/localstorage-helper';
import {
  MenuAiFinishedPayload,
  MenuAiProgressPayload,
} from '../../types/api/menuAi.types';

const SOCKET_URL = process.env.NX_API_URL;

/**
 * Socket hook for the Menu AI improve/save flow. Mirrors the server-side
 * `/menu` namespace and the `observe-menu` room used by MenuScrappingSocket.
 *
 * Flow:
 *   1. Caller POSTs .../improve (or .../save) to get a draftId.
 *   2. Caller invokes `observeMenu(locationId, brandId, true)` to subscribe.
 *   3. Server emits `menu_ai_progress` (→ onProgress) while it works, then
 *      `menu_ai_finished` with status 'ready' | 'saved' | 'failed' (→ onFinished).
 *   4. Caller GETs the draft / refreshes the live menu and unsubscribes.
 *
 * The callbacks are kept in refs so the socket effect stays keyed only on
 * `[refreshToken]` and never reconnects when the caller re-renders.
 */
const useMenuAiSocket = ({
  onProgress,
  onFinished,
}: {
  onProgress: (payload: MenuAiProgressPayload) => void;
  onFinished: (payload: MenuAiFinishedPayload) => void;
}) => {
  const socketRef = useRef<any>(null);
  const refreshToken = getRefreshToken();

  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    if (!refreshToken) return;

    const socket = io(SOCKET_URL, {
      path: '/menu',
      transports: ['websocket'],
      auth: {
        authorization: refreshToken, // Token in handshake
      },
    });

    socketRef.current = socket;

    socket.on('menu_ai_progress', (data: MenuAiProgressPayload) => {
      onProgressRef.current(data);
    });

    socket.on('menu_ai_finished', (data: MenuAiFinishedPayload) => {
      onFinishedRef.current(data);
    });

    socket.on('connect_error', (err) => {
      console.error('Menu AI socket connection error:', err.message);
    });

    // Clean up on unmount
    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const observeMenu = (
    locationId: string | number,
    brandId: string | number,
    subscribe: boolean
  ) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('observe-menu', {
        locationId,
        brandId,
        subscribe,
      });
    }
  };

  return { observeMenu };
};

export default useMenuAiSocket;
